// ============================================================================
// CONTENT DATA — practice areas, services, reviews, FAQ, bios
// ----------------------------------------------------------------------------
// NOTE: Reviews below are VERIFIED client reviews from Avvo (approved for use).
// Original client wording preserved (do not silently correct spelling).
// ============================================================================

import {
  Clapperboard, Trophy, FileSignature, Building2, BadgeCheck, Copyright,
} from "lucide-react";

// Six active practice areas — each links to a Services page anchor.
export const PRACTICE_AREAS = [
  { icon: Clapperboard, title: "Entertainment Law", anchor: "entertainment-law",
    blurb: "Counsel for artists, creators, producers, and media professionals navigating agreements and rights." },
  { icon: Trophy, title: "Sports Law", anchor: "sports-law",
    blurb: "Strategic representation for athletes and businesses operating within the sports industry." },
  { icon: FileSignature, title: "Contract Negotiation", anchor: "contract-negotiation",
    blurb: "Clear guidance for individuals and businesses entering important agreements." },
  { icon: Building2, title: "Real Estate Law & Closings", anchor: "real-estate",
    blurb: "Representation for buyers, sellers, owners, and investors in transactions and disputes." },
  { icon: BadgeCheck, title: "Trademark Law", anchor: "trademark-law",
    blurb: "Support to evaluate, register, and protect brand names, logos, and identifiers." },
  { icon: Copyright, title: "Copyright Matters", anchor: "copyright",
    blurb: "Guidance to document, license, and protect original creative works." },
];

export const VALUE_CARDS = [
  { n: "01", title: "Direct Access",
    body: "Clients receive clear communication and direct engagement with the legal team handling their matter." },
  { n: "02", title: "Strategic Communication",
    body: "Legal options are explained clearly, without unnecessary confusion or empty promises." },
  { n: "03", title: "Negotiation Strength",
    body: "The firm approaches negotiations with preparation, leverage, and a strong understanding of the client's goals." },
  { n: "04", title: "Competent Advocacy",
    body: "When a matter requires courtroom advocacy, the firm is prepared and equipped to protect the client's interests." },
  { n: "05", title: "Reliable Counsel",
    body: "Practical, dependable guidance that considers each client's professional, financial, and personal priorities." },
  { n: "06", title: "Ethical Practice",
    body: "Sensitive matters are treated with care, professionalism, and appropriate confidentiality." },
];

export const CREDIBILITY = [
  "Illinois Licensed Attorney",
  "Federal Court Admitted",
  "Thousands of Clients Represented",
  "Chicago-Rooted Counsel",
];

export const AUTHORITY = [
  "Thousands of Clients Represented",
  "More Than 1,000 Homeowners Defended",
  "Matters Across 13 Illinois Counties",
  "Illinois and Federal Court Admissions",
  "Experience Serving Athletes, Creators, Businesses, and Property Owners",
  "More Than a Decade of Legal Experience",
];

// Six active service sections for the Services page.
export const SERVICES = [
  { anchor: "entertainment-law", n: "01", title: "Entertainment Law",
    copy: "Legal guidance for artists, creators, producers, performers, media professionals, and entertainment businesses navigating agreements, opportunities, rights, and professional relationships.",
    items: ["Contract review and negotiation", "Talent and production agreements", "Licensing considerations", "Brand and intellectual-property coordination", "Dispute evaluation", "Business-structure guidance"] },
  { anchor: "sports-law", n: "02", title: "Sports Law",
    copy: "Strategic representation for professional athletes, aspiring professionals, sports personalities, and businesses operating within the sports industry.",
    items: ["Contract review", "Endorsement agreements", "Business and branding matters", "Professional-risk evaluation", "Dispute support", "Career-related legal strategy"] },
  { anchor: "contract-negotiation", n: "03", title: "Contract Negotiation",
    copy: "Clear contract guidance for individuals and businesses entering important professional, creative, commercial, or personal agreements.",
    items: ["Contract drafting", "Contract review", "Risk identification", "Negotiation strategy", "Amendment preparation", "Breach-of-contract disputes"] },
  { anchor: "real-estate", n: "04", title: "Real Estate Law and Closings",
    copy: "Legal representation for buyers, sellers, property owners, investors, and businesses navigating real estate transactions and disputes.",
    items: ["Residential and commercial closings", "Purchase and sale agreements", "Title and transaction review", "Foreclosure defense", "Property disputes", "Real estate investment matters"] },
  { anchor: "trademark-law", n: "05", title: "Trademark Law",
    copy: "Trademark support for entrepreneurs, creators, and businesses seeking to evaluate, register, and protect brand names, logos, and other source identifiers.",
    items: ["Preliminary trademark searches", "Application preparation", "Filing coordination", "USPTO office-action responses", "Brand-protection planning", "Trademark portfolio support"] },
  { anchor: "copyright", n: "06", title: "Copyright Matters",
    copy: "Guidance for creators, businesses, and rights holders seeking to understand, document, license, and protect original creative works.",
    items: ["Ownership evaluation", "Licensing agreements", "Assignment agreements", "Registration guidance", "Infringement concerns", "Collaboration and work-for-hire agreements"] },
];

export const FAQ = [
  { q: "When should I contact an attorney?",
    a: "It is generally wise to seek counsel early — before signing an agreement, when a dispute begins to develop, or as soon as you are aware of a legal deadline. Early guidance often creates more options and reduces exposure." },
  { q: "What should I bring to an initial consultation?",
    a: "Bring any documents relevant to your matter, such as contracts, correspondence, notices, or court paperwork, along with a brief written summary of what has happened and the outcome you are hoping to achieve." },
  { q: "Does submitting the website form make me a client?",
    a: "No. Submitting the form or contacting the firm does not create an attorney-client relationship. A relationship is established only after the firm formally agrees to represent you in writing." },
  { q: "Can the firm guarantee an outcome?",
    a: "No. No attorney can ethically guarantee a specific outcome. The firm focuses on thorough preparation, clear strategy, and diligent advocacy on your behalf." },
  { q: "Does the firm represent clients outside Chicago?",
    a: "The firm is Chicago-rooted and has handled matters across numerous Illinois counties. Whether the firm can assist with a particular matter depends on the specifics, which can be discussed during a consultation." },
  { q: "How are legal fees determined?",
    a: "Fees depend on the type and complexity of the matter. Some matters are handled on a flat-fee basis and others hourly. Fee arrangements are discussed directly so you understand the structure before moving forward." },
];

// Verified Avvo reviews (approved for use). Preserve original client wording — do not correct spelling.
export const REVIEWS = [
  { name: "Carrie", date: "June 7, 2021", title: "My perfect closing attorney",
    quote: "Ronald K. Nolen is an angel. He was the perfect closing attorney & very knowledgeable about his craft. He made sure that all of my demands were met & that we closed when we were supposed to. Ronald gave me lots of reassurance during the entire process. That was very important to me, too.",
    label: "Hired Attorney" },
  { name: "Veronica", date: "May 14, 2021", title: "Buyer Attorney for Closing",
    quote: "I am grateful for having Ronald Nolen as my attorney through my housing process. He explained the housing process to me as I was a first time buyer and ensured I was meeting my timelines to reach closing.",
    label: "Hired Attorney" },
  { name: "Amanuel", date: "December 6, 2017", title: "Real Estate Closing",
    quote: "Attorney Nolen represented me during my real estate closing. He was incredibly attentive throughout the entire process. He always responded to my questions quickly and thoroughly. He walked me through the entire process and made sure I understood the steps and the terms that were being used.",
    label: "Hired Attorney" },
  { name: "Byron", date: "November 10, 2015", title: "Ronald Nolen",
    quote: "Ronald was compitent and compasionate. It is my opinion, because he is a small firm, I was able to have direct access to him when needed so that I was confidate my concerns were being heard. I would definately recomend Mr. Nolen for your legal needs.",
    label: "Hired Attorney" },
  { name: "Selena", date: "November 11, 2015", title: "Diligent, thorough, always accessible, and affordable!",
    quote: "We hired Ron to help with the closing of our new home build. He read through our builders contract with a fine tooth comb. He provided invaluable insight on language in the contract that could have been potentially harmful to us as a purchaser.",
    label: "Hired Attorney" },
  { name: "Latasha", date: "July 25, 2017", title: "Ronald K Nolen",
    quote: "He is the best lawyer, he is compassionate, caring, reliable, dependable and available. He becomes more than a great attorney, he becomes a great friend and I have been grateful and blessed to have the honor of having him represent me in the professional fashion and manor as he's done.",
    label: "Hired Attorney" },
  { name: "Nikita", date: "October 20, 2015", title: "Family Court",
    quote: "Attorney Nolen came to court prepared. He accepted my case and represented me professionally. I was very pleased with the outcome of my case.",
    label: "Hired Attorney" },
  { name: "Temilolu", date: "January 10, 2016", title: "Client",
    quote: "Attorney Nolen is a excellent Attorney/Officer of the court. Attorney Nolen is very knowledgeable and skilled in litigation. Attorney Nolen is very throughout and detailed. Attorney Nolen kept me updated on my case and is always prepared for court.",
    label: "Hired Attorney" },
];

// Six featured on the Client Reviews page.
export const REVIEWS_FEATURED = REVIEWS.slice(0, 6);

// Three strongest for the homepage preview (Carrie, Amanuel, Byron).
export const REVIEWS_PREVIEW = [REVIEWS[0], REVIEWS[2], REVIEWS[3]];

export const PRACTICE_AREA_OPTIONS = [
  "Entertainment Law", "Sports Law", "Contract Matter", "Real Estate", "Trademark", "Copyright", "Other",
];

export const URGENCY_OPTIONS = [
  "General Inquiry", "Time-Sensitive", "Existing Deadline or Court Date", "Unsure",
];

export const CONTACT_METHOD_OPTIONS = ["Phone", "Email"];
